import React, { useRef, useState } from "react";
import EmptyMediaHolder from "./EmptyMediaHolder";

interface ImageMediaComponentProps {
  imageUrl: string | null | undefined; // The URL of the image to display
  altText?: string; // Alternative text for the image
  onOpenLibrary?: () => void;
  onUploadLocal?: (file: File) => void;
  onRemove?: () => void; // Detaches the image from the post; the file itself is kept
  isUploading?: boolean; // Whether the image is currently being uploaded
}

const ImageMediaComponent: React.FC<ImageMediaComponentProps> = ({
  imageUrl,
  altText = "Post Image",
  onOpenLibrary,
  onUploadLocal,
  onRemove,
  isUploading = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = onOpenLibrary || onUploadLocal;

  const handleEditClick = () => setShowMenu((prev) => !prev);

  const renderPopover = (positionClasses: string) => {
    if (!showMenu || !canEdit) return null;
    return (
      <>
        <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
        <div className={`absolute left-1/2 z-50 flex w-48 -translate-x-1/2 flex-col rounded-xl border border-theme-border bg-theme-surface p-1.5 shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 ${positionClasses}`}>
          {onOpenLibrary && (
            <button
              type="button"
              onClick={() => {
                setShowMenu(false);
                onOpenLibrary();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-theme-fg transition-colors hover:bg-theme-secondary/10"
            >
              <span className="material-icons text-[18px] text-theme-fg-muted">photo_library</span>
              My Library
            </button>
          )}
          {onOpenLibrary && onUploadLocal && <div className="my-1 h-px w-full bg-theme-border/50" />}
          {onUploadLocal && (
            <button
              type="button"
              onClick={() => {
                setShowMenu(false);
                fileInputRef.current?.click();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-theme-fg transition-colors hover:bg-theme-secondary/10"
            >
              <span className="material-icons text-[18px] text-theme-fg-muted">upload_file</span>
              Local files
            </button>
          )}
        </div>
      </>
    );
  };

  if (!imageUrl && !isUploading) {
    return (
      <div className="relative w-full">
        <EmptyMediaHolder type="image" onEdit={canEdit ? handleEditClick : undefined} />
        {renderPopover("top-1/2 -translate-y-1/2")}
        {onUploadLocal && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onUploadLocal(e.target.files[0]);
                e.target.value = "";
              }
            }}
          />
        )}
      </div>
    );
  }

  return (
    <figure className="w-full relative flex flex-col items-center justify-start">
      {/* Image — key remounts when URL changes so replacements show without a full refresh. */}
      <img
        key={imageUrl || (isUploading ? "uploading" : "no-src")}
        className="rounded-lg"
        src={imageUrl || undefined}
        alt={altText}
        style={{ 
          maxWidth: '100%', 
          width: 'auto', 
          height: 'auto',
          objectFit: 'contain',
          display: 'block'
        }}
      />

      {/* Loading Overlay */}
      {isUploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-20 backdrop-blur-sm rounded-lg">
          <div className="relative">
            {/* Spinning loader */}
            <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-white font-semibold text-lg">Uploading...</p>
          <p className="mt-1 text-white/70 text-sm">Please wait</p>
        </div>
      )}

      {/* Replace / Remove actions */}
      {(canEdit || onRemove) && (
        <figcaption className="mt-3 flex items-center justify-center gap-2 relative">
          {renderPopover("bottom-full mb-2")}
          {canEdit && (
            <button
              type="button"
              onClick={handleEditClick}
              className="inline-flex items-center gap-2 rounded-lg border border-theme-border bg-theme-surface px-4 py-2 text-sm font-semibold text-theme-fg shadow-sm transition-all hover:bg-theme-secondary/10 hover:border-brand-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isUploading}
            >
              <span className="material-icons text-lg text-theme-fg-muted">
                sync
              </span>
              Replace Image
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => {
                setShowMenu(false);
                onRemove();
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-theme-border bg-theme-surface px-4 py-2 text-sm font-semibold text-red-600 shadow-sm transition-all hover:bg-red-500/10 hover:text-red-700 hover:border-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isUploading}
              title="Remove this image from the post"
            >
              <span className="material-icons text-lg">delete_outline</span>
              Remove Image
            </button>
          )}
          {onUploadLocal && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  onUploadLocal(e.target.files[0]);
                  e.target.value = "";
                }
              }}
            />
          )}
        </figcaption>
      )}
    </figure>
  );
};

export default ImageMediaComponent;
